import { useEffect, useMemo, useState } from "react";
import { Download, Landmark, Search, X, MessageSquare, Phone, MapPin, IndianRupee, Clock, Rocket, AlertTriangle, TimerReset, RotateCcw, GitBranch, RefreshCw, UserCheck, Loader2, CheckCircle2, ArrowUpDown, Ban, MoreVertical, Eye, Zap, HourglassIcon } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuPortal } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { LoanIcon } from "@/lib/loanIcons";
import api, { errorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useRefocus } from "@/lib/refocus";
import BoostModal from "@/components/app/BoostModal";
import PipelineTimeline from "@/components/app/PipelineTimeline";
import RejectionHistory from "@/components/app/RejectionHistory";
import LoadingSkeleton from "@/components/app/LoadingSkeleton";
import Paginator from "@/components/app/Paginator";

function statusBadge(s, lead) {
  // Admin direct-assign auto-takes the case — surface it as a green "ASSIGNED"
  // pill in the pool so the row reads unambiguously.
  if (lead && lead.assignment_decided_by_role === "admin" && lead.bank_assignment_status === "assigned") {
    return { className: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", label: "Assigned" };
  }
  if (s === "in_bank_pool") return { className: "bg-blue-500/20 text-blue-300 border-blue-500/30", label: "In Bank Pool" };
  if (s === "taken")        return { className: "bg-amber-500/20 text-amber-300 border-amber-500/30", label: "Bank Reviewing" };
  if (s === "ok")           return { className: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", label: "Approved" };
  return { className: "bg-slate-700/40 text-slate-300", label: s || "-" };
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || "—"; }
}

// SLA bucket → row/badge styling. Only leads still in in_bank_pool are aged.
// `sticky` is the OPAQUE bg used on `position:sticky` cells so scrolling
// content underneath doesn't bleed through the frozen columns.
const SLA = {
  critical: {
    row:    "bg-red-500/10 hover:bg-red-500/15 border-l-4 border-l-red-500",
    sticky: "bg-[#2a1414]",
    badge:  "bg-red-500/25 text-red-200 border-red-500/50",
    label:  "48H+",
  },
  warning: {
    row:    "bg-amber-500/10 hover:bg-amber-500/15 border-l-4 border-l-amber-500",
    sticky: "bg-[#2a2214]",
    badge:  "bg-amber-500/25 text-amber-200 border-amber-500/50",
    label:  "24H+",
  },
  ok: { row: "", sticky: "bg-slate-950", badge: "bg-slate-700/40 text-slate-300 border-slate-600", label: "OK" },
};

function fmtWaiting(hrs) {
  if (hrs == null) return "—";
  if (hrs < 1) return `${Math.max(1, Math.round(hrs * 60))}m`;
  if (hrs < 24) return `${Math.floor(hrs)}h ${Math.round((hrs % 1) * 60)}m`;
  const d = Math.floor(hrs / 24);
  const h = Math.floor(hrs % 24);
  return `${d}d ${h}h`;
}

function DetailsDialog({ lead, open, onClose, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!lead) return;
    setLoading(true);
    try { const r = await api.get(`/staff/leads/${lead.lead_id}/chat`); setMessages(r.data.messages || []); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (!open) return;
    setMessages([]); setText(""); load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [open, lead?.lead_id]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const r = await api.post(`/staff/leads/${lead.lead_id}/chat`, { text: t });
      setMessages(m => [...m, r.data.message]);
      setText("");
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSending(false); }
  };
  const roleLabel = { office_staff: "Telecaller", bank_staff: "Bank", admin: "Admin" };
  const sb = statusBadge(lead?.status, lead);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ibp-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-amber-400 flex items-center gap-2 flex-wrap">
            {lead?.name} <span className="text-xs text-slate-500 font-mono-alt font-normal">{lead?.lead_id}</span>
            <Badge className={sb.className + " text-[10px] uppercase tracking-wider"}>{sb.label}</Badge>
          </DialogTitle>
        </DialogHeader>
        {lead && (
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-slate-500"/> <span className="font-mono-alt">{lead.phone}</span></div>
              <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-slate-500"/> {lead.district}</div>
              <div className="flex items-center gap-2 text-white"><LoanIcon loan={{ name: lead.loan_type }} className="w-3.5 h-3.5 text-amber-400"/> {parseFloat(lead.amount).toLocaleString("en-IN")} · <span className="font-bold">{lead.loan_type}</span></div>
              <div className="flex items-center gap-2 text-slate-400"><Clock className="w-3 h-3 text-slate-500"/> Pushed: <span className="text-white">{fmtDate(lead.bank_pool_at || lead.created_at)}</span></div>
            </div>
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Assigned Bank</div>
              {lead.bank_name
                ? <div className="text-white"><Landmark className="w-3 h-3 inline mr-1 text-blue-400"/> <b>{lead.bank_name}</b> {lead.bank_staff_name && <span className="text-slate-400 text-xs">· {lead.bank_staff_name}</span>}</div>
                : <div className="text-slate-500 italic text-xs">Not yet claimed</div>}
              <div className="text-[10px] uppercase tracking-widest text-slate-500 pt-2">Assigned Telecaller</div>
              <div className="text-white text-xs">{lead.assigned_office_staff_name || "—"}</div>
            </div>
          </div>
        )}

        {/* Rejection history (if any) */}
        {lead?.rejections?.length > 0 && (
          <div className="mt-4"><RejectionHistory lead={lead}/></div>
        )}

        {/* 9-Stage Pipeline (view-only) */}
        {lead && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
              <GitBranch className="w-3 h-3"/> 9-Stage Pipeline
            </div>
            <div className="rounded-lg border border-white/5 bg-slate-950/40 p-3">
              <PipelineTimeline lead={lead} compact/>
            </div>
          </div>
        )}
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2"><MessageSquare className="w-3 h-3"/> Bank Chat Thread</div>
          <div className="h-56 overflow-y-auto space-y-2 bg-slate-950/50 border border-white/5 rounded-lg p-3">
            {loading && <div className="text-center text-slate-500 text-xs py-4">Loading…</div>}
            {!loading && messages.length === 0 && <div className="text-center text-slate-500 text-xs py-6" data-testid="ibp-chat-empty">No messages yet.</div>}
            {messages.map(m => {
              const isMe = m.by_id === currentUserId;
              const isBank = m.by_role === "bank_staff";
              return (
                <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`ibp-chat-${m.id}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-xs ${isMe ? "bg-amber-500/20 border border-amber-500/30 text-amber-50" : isBank ? "bg-blue-500/10 border border-blue-500/30 text-blue-50" : "bg-slate-800 border border-white/5 text-slate-200"}`}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] uppercase tracking-widest font-bold">{m.by_name}</span>
                      <span className="text-[9px] uppercase tracking-widest opacity-60">{roleLabel[m.by_role] || m.by_role}</span>
                    </div>
                    <div className="whitespace-pre-wrap">{m.text}</div>
                    <div className="text-[9px] opacity-50 mt-1">{fmtDate(m.at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex gap-2">
            <textarea rows={2} value={text} onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type a message… (Enter to send)"
              data-testid="ibp-chat-input"
              className="flex-1 bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500"/>
            <Button data-testid="ibp-chat-send" onClick={send} disabled={sending || !text.trim()} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-md px-4 disabled:opacity-40">Send</Button>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button data-testid="ibp-details-close" variant="ghost" onClick={onClose} className="text-slate-300 hover:text-white"><X className="w-4 h-4 mr-2"/> Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function InBankPoolPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ leads: [], districts: [], banks: [], total: 0 });
  const [allDistricts, setAllDistricts] = useState([]);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [district, setDistrict] = useState("all");
  const [bank, setBank] = useState("all");
  const [selected, setSelected] = useState(null);
  const [boostOpen, setBoostOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [staffDirectory, setStaffDirectory] = useState([]);
  const [rowStaff, setRowStaff] = useState({});      // { [lead_id]: staff_id }
  const [assigning, setAssigning] = useState({});    // { [lead_id]: bool }
  const [confirm, setConfirm] = useState(null);      // { lead, staff }
  const [rejectLead, setRejectLead] = useState(null); // lead being rejected
  const [rejectReasons, setRejectReasons] = useState([]);
  const [rejectForm, setRejectForm] = useState({ reason: "", remarks: "" });
  const [rejecting, setRejecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // Set<lead_id>
  const [bulkReject, setBulkReject] = useState(null); // { reason, remarks } | null
  const [bulkBusy, setBulkBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [sortOrder, setSortOrder] = useState("non_assigned_first"); // non_assigned_first | assigned_first | newest | oldest

  const [loaded, setLoaded] = useState(false);
  // Debounce the search box → server call after 350ms idle.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);
  // Reset to page 1 whenever a filter that affects the server changes.
  useEffect(() => { setPage(1); }, [debouncedQ, district, sortOrder]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      if (debouncedQ) params.set("q", debouncedQ);
      if (district !== "all") params.set("district", district);
      params.set("sort", sortOrder);
      const r = await api.get(`/reports/in-bank-pool?${params.toString()}`);
      setData(r.data);
      setLoaded(true);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  };
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ page: "1", page_size: "0", sort: sortOrder });
      if (debouncedQ) params.set("q", debouncedQ);
      if (district !== "all") params.set("district", district);
      const r = await api.get(`/reports/in-bank-pool?${params.toString()}`);
      const rows = (r.data.leads || []).filter(l => bank === "all" || (bank === "__none__" ? !l.bank_name : l.bank_name === bank));
      const cols = [["Lead ID", "lead_id"], ["Name", "name"], ["Phone", "phone"], ["District", "district"], ["Loan Type", "loan_type"], ["Amount", "amount"], ["Status", "status"], ["Bank", "bank_name"], ["Bank Staff", l => l.bank_staff_name || (l.assigned_bank_staff_names || []).join("; ")], ["Assignment", "bank_assignment_status"], ["Waiting Hours", "waiting_hours"], ["SLA", "sla_bucket"], ["Bank Pool At", "bank_pool_at"], ["Created At", "created_at"]];
      const esc = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const csv = [cols.map(c => esc(c[0])).join(","), ...rows.map(l => cols.map(([, k]) => esc(typeof k === "function" ? k(l) : l[k])).join(","))].join("\n");
      const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `in-bank-pool-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} cases`);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setExporting(false); }
  };
  const loadStaffDirectory = async () => {
    if (user?.role !== "admin") return;
    try {
      const r = await api.get("/staff/bank-staff-directory");
      setStaffDirectory(r.data.staff || []);
    } catch { /* silent — dropdown will show 'no staff' */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [page, debouncedQ, district, sortOrder]);
  useRefocus(() => { load(); loadStaffDirectory(); });
  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/districts").then(r => setAllDistricts((r.data || []).map(d => d.name))).catch(() => {});
    loadStaffDirectory();
    api.get("/admin/bank-pool/reject-reasons").then(r => setRejectReasons(r.data?.reasons || [])).catch(() => {});
    // eslint-disable-next-line
  }, [user?.role]);

  const doAssign = async () => {
    if (!confirm) return;
    const { lead, staff } = confirm;
    setAssigning(a => ({ ...a, [lead.lead_id]: true }));
    try {
      await api.post(`/staff/bank-requests/${lead.lead_id}/assign`, { staff_ids: [staff.id] });
      toast.success(`Assigned ${lead.name} to ${staff.name}`);
      setRowStaff(rs => { const { [lead.lead_id]: _drop, ...rest } = rs; return rest; });
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setAssigning(a => { const { [lead.lead_id]: _drop, ...rest } = a; return rest; });
    }
  };

  const openReject = (lead) => {
    setRejectForm({ reason: "", remarks: "" });
    setRejectLead(lead);
  };
  const doReject = async () => {
    if (!rejectLead || !rejectForm.reason) return;
    const lead = rejectLead;
    setRejecting(true);
    // Optimistic: hide the row immediately so the count drops from 49 → 48.
    setData(d => ({ ...d, leads: d.leads.filter(x => x.lead_id !== lead.lead_id),
                        total: Math.max(0, (d.total || 0) - 1) }));
    try {
      const res = await api.post("/admin/bank-pool/reject", {
        lead_id: lead.lead_id,
        reason: rejectForm.reason,
        remarks: rejectForm.remarks.trim(),
      });
      toast.success(`Case Rejected · ${lead.name} · ${res.data?.reason || rejectForm.reason}`);
      setRejectLead(null);
      // Silent refetch to reconcile server totals + waiting counters.
      load();
    } catch (e) {
      toast.error(errorMessage(e));
      // Roll back the optimistic hide by refetching authoritative state.
      load();
    } finally {
      setRejecting(false);
    }
  };

  // Client-side bank filter still applies within the current page — search /
  // district / sort are server-side so pagination totals stay accurate.
  const filtered = data.leads.filter(l => {
    const matchB = bank === "all" || (bank === "__none__" ? !l.bank_name : l.bank_name === bank);
    return matchB;
  });

  // ------------------- Bulk selection helpers -------------------
  const toggleRow = (leadId) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
    return next;
  });
  const selectableIds = filtered.map(l => l.lead_id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));
  const someSelected = selectableIds.some(id => selectedIds.has(id));
  const togglePageSelect = () => setSelectedIds(prev => {
    const next = new Set(prev);
    if (allSelected) selectableIds.forEach(id => next.delete(id));
    else selectableIds.forEach(id => next.add(id));
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());

  const doBulkAssignSameStaff = async (staff) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    const ids = [...selectedIds];
    let ok = 0, failed = 0;
    for (const id of ids) {
      try {
        await api.post(`/staff/bank-requests/${id}/assign`, { staff_ids: [staff.id] });
        ok++;
      } catch { failed++; }
    }
    toast[failed ? "error" : "success"](`Assigned ${ok}/${ids.length} to ${staff.name}${failed ? ` · ${failed} failed` : ""}`);
    clearSelection();
    setBulkBusy(false);
    load();
  };
  const openBulkReject = () => setBulkReject({ reason: "", remarks: "" });
  const doBulkReject = async () => {
    if (!bulkReject?.reason || selectedIds.size === 0) return;
    setBulkBusy(true);
    const ids = [...selectedIds];
    try {
      const r = await api.post("/admin/bank-pool/bulk-reject", {
        lead_ids: ids, reason: bulkReject.reason, remarks: bulkReject.remarks || "",
      });
      const rej = r.data?.rejected?.length || 0;
      const skip = r.data?.skipped?.length || 0;
      toast.success(`Rejected ${rej}/${ids.length}${skip ? ` · ${skip} skipped` : ""}`);
      clearSelection();
      setBulkReject(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setBulkBusy(false); }
  };

  const slaCounts = filtered.reduce((acc, l) => {
    if (l.sla_bucket === "critical") acc.critical += 1;
    else if (l.sla_bucket === "warning") acc.warning += 1;
    return acc;
  }, { warning: 0, critical: 0 });

  return (
    <div className="card-surface p-6" data-testid="in-bank-pool-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center"><Landmark className="w-5 h-5 text-blue-400"/></div>
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-400 font-bold">Bank Pipeline</div>
            <div className="font-heading text-xl text-white">In Bank Pool <span className="text-slate-500 text-sm font-normal">· {loaded ? `${filtered.length} on page · ${data.total || 0} total` : "—"}</span></div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2"/>
            <Input data-testid="ibp-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search name / phone / ID" className="bg-slate-950 border-slate-700 pl-9 min-w-[240px]"/>
          </div>
          <select data-testid="ibp-district-filter" value={district} onChange={e => setDistrict(e.target.value)} className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm">
            <option value="all">All Districts</option>
            {data.districts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select data-testid="ibp-bank-filter" value={bank} onChange={e => setBank(e.target.value)} className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm">
            <option value="all">All Banks</option>
            <option value="__none__">Not yet claimed</option>
            {data.banks.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select
            data-testid="ibp-sort-select"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            title="How to order the pool"
            className="h-9 px-3 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold uppercase tracking-widest focus:outline-none focus:border-amber-500"
          >
            <option value="non_assigned_first">⏳ Non-Assigned First</option>
            <option value="assigned_first">✅ Assigned First</option>
            <option value="newest">▼ Newest First</option>
            <option value="oldest">▲ Oldest First</option>
          </select>
          <button
            type="button"
            onClick={load}
            data-testid="ibp-refresh"
            title="Refresh"
            className="w-9 h-9 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center justify-center text-slate-400"
          ><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}/></button>
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting}
            data-testid="ibp-export-csv"
            title="Download filtered list as CSV"
            className="h-9 px-3 rounded-md bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/40 text-emerald-300 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
          >{exporting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>} CSV</button>
          {user?.role === "admin" && (
            <Button data-testid="boost-open-btn" onClick={() => setBoostOpen(true)}
              className="bg-orange-500 hover:bg-orange-400 text-slate-950 font-bold rounded-full px-4 py-2 shadow-lg shadow-orange-500/30">
              <Rocket className="w-4 h-4 mr-2"/> BOOST
            </Button>
          )}
        </div>
      </div>

      {user?.role === "admin" && (
        <BoostModal open={boostOpen} onClose={() => setBoostOpen(false)} districts={allDistricts} onChange={load}/>
      )}

      {loaded && data.stats && user?.role === "admin" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-testid="ibp-summary-cards">
          <div className="rounded-xl border border-emerald-500/40 bg-[#0e1f1a] p-4 flex items-center gap-4"
               data-testid="ibp-summary-assigned">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-400"/>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">Assigned</div>
              <div className="text-white font-heading text-lg leading-tight">
                {data.stats.assigned_count} Cases
                <span className="text-emerald-300 font-mono-alt text-sm ml-2">
                  ₹{Math.round(data.stats.assigned_amount || 0).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="text-[10px] text-slate-500">Total loan amount of assigned cases</div>
            </div>
          </div>
          <div className="rounded-xl border border-orange-500/40 bg-[#1f180e] p-4 flex items-center gap-4"
               data-testid="ibp-summary-non-assigned">
            <div className="w-10 h-10 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center">
              <Clock className="w-5 h-5 text-orange-400"/>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">Non-Assigned</div>
              <div className="text-white font-heading text-lg leading-tight">
                {data.stats.non_assigned_count} Cases
                <span className="text-orange-300 font-mono-alt text-sm ml-2">
                  ₹{Math.round(data.stats.non_assigned_amount || 0).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="text-[10px] text-slate-500">Yet to assign to staff</div>
            </div>
          </div>
        </div>
      )}

      {(slaCounts.warning > 0 || slaCounts.critical > 0) && (
        <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="sla-summary">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400"/> SLA Alerts
          </div>
          {slaCounts.critical > 0 && (
            <Badge data-testid="sla-critical-count" className="bg-red-500/20 text-red-200 border-red-500/40 uppercase tracking-widest text-[10px] py-1 px-3">
              <TimerReset className="w-3 h-3 mr-1"/> {slaCounts.critical} stalled 48h+
            </Badge>
          )}
          {slaCounts.warning > 0 && (
            <Badge data-testid="sla-warning-count" className="bg-amber-500/20 text-amber-200 border-amber-500/40 uppercase tracking-widest text-[10px] py-1 px-3">
              <Clock className="w-3 h-3 mr-1"/> {slaCounts.warning} waiting 24h+
            </Badge>
          )}
          <div className="text-[11px] text-slate-500">Highlighted rows are cases the bank hasn't claimed yet.</div>
        </div>
      )}

      {loading && !loaded && (
        <LoadingSkeleton rows={5} label="Loading bank pipeline…" subLabel="Fetching cases · districts · SLA counters" testid="ibp-loading"/>
      )}

      {/* ---------- Bulk Action Bar (admin only, appears when rows selected) ---------- */}
      {user?.role === "admin" && selectedIds.size > 0 && (
        <div className="mb-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 flex flex-wrap items-center gap-3 sticky top-2 z-20 backdrop-blur"
             data-testid="ibp-bulk-bar">
          <div className="text-sm font-bold text-amber-200">
            {selectedIds.size} selected
          </div>
          <div className="h-4 w-px bg-amber-500/30"/>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="ibp-bulk-assign-trigger"
                disabled={bulkBusy}
                className="h-8 px-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-40">
                <UserCheck className="w-3.5 h-3.5"/> Bulk Assign
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-slate-900 border-slate-700 text-white max-h-[60vh] overflow-y-auto">
              <DropdownMenuLabel>Pick a bank staff</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-slate-800"/>
              {staffDirectory.length === 0 && <DropdownMenuItem disabled>No active bank staff</DropdownMenuItem>}
              {staffDirectory.slice(0, 40).map(s => (
                <DropdownMenuItem key={s.id} onClick={() => doBulkAssignSameStaff(s)}
                  className="text-xs cursor-pointer focus:bg-amber-500/20">
                  <UserCheck className="w-3.5 h-3.5 mr-2 text-amber-400"/>
                  {(s.name || "?").toUpperCase()} · {(s.district || "—").toUpperCase()} · {s.specialization || "General"}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            data-testid="ibp-bulk-reject-btn"
            onClick={openBulkReject}
            disabled={bulkBusy}
            className="h-8 px-3 rounded-full bg-red-500 hover:bg-red-400 text-white font-bold text-xs uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-40">
            <Ban className="w-3.5 h-3.5"/> Bulk Reject
          </button>
          <div className="ml-auto">
            <button
              data-testid="ibp-bulk-clear"
              onClick={clearSelection}
              className="h-8 px-3 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-xs">
              Clear
            </button>
          </div>
        </div>
      )}

      {loaded && <div className="overflow-x-auto">
        <div className="min-w-[1100px] relative">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-950/95 backdrop-blur z-10">
              <TableRow className="border-white/5">
                {user?.role === "admin" && (
                  <TableHead className="w-10 sticky left-0 bg-slate-950/95 z-20">
                    <Checkbox
                      data-testid="ibp-select-all"
                      checked={allSelected}
                      onCheckedChange={togglePageSelect}
                      aria-label="Select all on page"
                      className="border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                    />
                  </TableHead>
                )}
                {user?.role === "admin" && <TableHead className="w-8"/>}
                <TableHead>Lead Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>District</TableHead>
                <TableHead>Loan Type</TableHead>
                <TableHead>Assigned Bank</TableHead>
                <TableHead>Pushed Date</TableHead>
                <TableHead>Waiting</TableHead>
                <TableHead className="sticky right-[220px] bg-slate-950/95 z-20 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.6)]">Status</TableHead>
                {user?.role === "admin" && (
                  <TableHead className="min-w-[220px] sticky right-0 bg-slate-950/95 z-20 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.8)]">Action</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(l => {
                const sb = statusBadge(l.status, l);
                const sla = SLA[l.sla_bucket] || null;
                const isChecked = selectedIds.has(l.lead_id);
                const rowBg = sla?.row || "";
                // Sticky columns need an opaque background matching the row's
                // hover / SLA tint so text underneath doesn't bleed through.
                const stickyBg = sla?.sticky || "bg-slate-950";
                return (
                  <TableRow key={l.lead_id} data-testid={`ibp-row-${l.lead_id}`}
                    data-sla={l.sla_bucket || "none"}
                    className={`border-white/5 cursor-pointer hover:bg-white/5 ${rowBg}`}
                    onClick={() => setSelected(l)}>
                    {user?.role === "admin" && (
                      <TableCell className={`sticky left-0 z-10 ${stickyBg}`} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          data-testid={`ibp-row-check-${l.lead_id}`}
                          checked={isChecked}
                          onCheckedChange={() => toggleRow(l.lead_id)}
                          className="border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                        />
                      </TableCell>
                    )}
                    {user?.role === "admin" && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <RowQuickMenu
                          lead={l}
                          staffList={staffDirectory}
                          onAssign={(staff) => setConfirm({ lead: l, staff })}
                          onReject={() => openReject(l)}
                          onDetails={() => setSelected(l)}
                        />
                      </TableCell>
                    )}
                    <TableCell className="text-white">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{l.name}</span>
                        {l.unread_chat > 0 && (
                          <span
                            data-testid={`ibp-unread-${l.lead_id}`}
                            title={`${l.unread_chat} unread chat message(s) — open Details to view`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold uppercase tracking-widest animate-pulse">
                            <MessageSquare className="w-2.5 h-2.5"/> {l.unread_chat}
                          </span>
                        )}
                        {l.is_boosted && (
                          <Badge data-testid={`boosted-badge-${l.lead_id}`}
                            className="bg-orange-500/25 text-orange-300 border-orange-500/40 text-[9px] uppercase tracking-widest font-bold whitespace-nowrap">
                            <Rocket className="w-2.5 h-2.5 mr-1"/>BOOSTED
                          </Badge>
                        )}
                        {sla && l.sla_bucket !== "ok" && (
                          <Badge data-testid={`sla-badge-${l.lead_id}`}
                            className={sla.badge + " text-[9px] uppercase tracking-widest font-bold whitespace-nowrap"}>
                            <AlertTriangle className="w-2.5 h-2.5 mr-1"/>{sla.label}
                          </Badge>
                        )}
                        {l.rejections && l.rejections.length > 0 && (
                          <Badge data-testid={`rej-badge-${l.lead_id}`}
                            className="bg-red-500/20 text-red-200 border-red-500/40 text-[9px] uppercase tracking-widest font-bold whitespace-nowrap"
                            title={`Previously rejected ${l.rejections.length} time(s) — click for details`}>
                            <RotateCcw className="w-2.5 h-2.5 mr-1"/>Rejected {l.rejections.length}x
                          </Badge>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono-alt">{l.lead_id}</div>
                      {l.rejections && l.rejections.length > 0 && (
                        <div className="text-[10px] text-red-300/80 mt-1 truncate max-w-[280px]"
                             title={l.rejections[l.rejections.length-1].reason || ""}>
                          Last: <b>{(l.rejections[l.rejections.length-1].stage || "").toUpperCase()}</b>
                          {l.rejections[l.rejections.length-1].bank_name && (<> · {l.rejections[l.rejections.length-1].bank_name}</>)}
                          {l.rejections[l.rejections.length-1].reason && (<> — {l.rejections[l.rejections.length-1].reason}</>)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono-alt">{l.phone}</TableCell>
                    <TableCell className="text-slate-300">{l.district}</TableCell>
                    <TableCell className="text-slate-300">
                      <div className="flex items-center gap-1.5">
                        <LoanIcon loan={{ name: l.loan_type }} className="w-3.5 h-3.5 text-amber-400"/>
                        <span>{l.loan_type}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {l.bank_assignment_status === "assigned" && l.assigned_bank_staff_names
                        ? <div>
                            <div className="text-[9px] uppercase tracking-widest text-emerald-400 font-bold flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5"/>Assigned to</div>
                            <div className="text-white text-sm font-bold">{l.assigned_bank_staff_names}</div>
                            {l.bank_name && <div className="text-[10px] text-slate-500">{l.bank_name}</div>}
                          </div>
                        : l.bank_name
                          ? <div><span className="text-white">{l.bank_name}</span>{l.bank_staff_name && <div className="text-[10px] text-slate-500">{l.bank_staff_name}</div>}</div>
                          : <span className="text-slate-500 text-xs italic">Not yet claimed</span>}
                    </TableCell>
                    <TableCell className="text-slate-300 text-xs">{fmtDate(l.bank_pool_at || l.created_at)}</TableCell>
                    <TableCell data-testid={`ibp-waiting-${l.lead_id}`} className="text-xs whitespace-nowrap">
                      {l.waiting_hours == null
                        ? <span className="text-slate-600">—</span>
                        : <span className={l.sla_bucket === "critical" ? "text-red-300 font-bold" : l.sla_bucket === "warning" ? "text-amber-300 font-bold" : "text-slate-300"}>
                            {fmtWaiting(l.waiting_hours)}
                          </span>}
                    </TableCell>
                    <TableCell className={`sticky right-[220px] z-10 ${stickyBg} shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.6)]`}>
                      <Badge className={sb.className + " text-[10px] uppercase tracking-wider whitespace-nowrap"}>{sb.label}</Badge>
                    </TableCell>
                    {user?.role === "admin" && (
                      <TableCell className={`sticky right-0 z-10 ${stickyBg} shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.8)]`} onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 min-w-[200px]">
                          <AssignAction
                            lead={l}
                            staffList={staffDirectory}
                            selectedStaffId={rowStaff[l.lead_id] || ""}
                            onChangeStaff={(sid) => setRowStaff(rs => ({ ...rs, [l.lead_id]: sid }))}
                            onConfirm={(staff) => setConfirm({ lead: l, staff })}
                            assigning={!!assigning[l.lead_id]}
                          />
                          <button
                            type="button"
                            data-testid={`reject-btn-${l.lead_id}`}
                            onClick={() => openReject(l)}
                            title="Reject / Close this case"
                            className="shrink-0 w-9 h-9 rounded-full bg-red-500/15 hover:bg-red-500/30 border border-red-500/40 text-red-300 hover:text-red-200 flex items-center justify-center transition-colors"
                          ><Ban className="w-4 h-4"/></button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {filtered.length === 0 && <TableRow><TableCell colSpan={user?.role === "admin" ? 11 : 8} className="text-center text-slate-500 py-10">No leads in the bank pipeline match this filter.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>}

      {loaded && data.total > 0 && (
        <Paginator page={page} pageSize={pageSize} total={data.total} onPage={setPage} testid="ibp-pager"/>
      )}

      <DetailsDialog lead={selected} open={!!selected} onClose={() => setSelected(null)} currentUserId={user?.id}/>

      {user?.role === "admin" && (
        <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
          <DialogContent className="bg-slate-900 border-amber-500/30 text-white max-w-md" data-testid="assign-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-amber-400 flex items-center gap-2">
                <UserCheck className="w-5 h-5"/> Confirm Assignment
              </DialogTitle>
            </DialogHeader>
            {confirm && (
              <div className="space-y-4">
                <div className="text-sm text-slate-300">
                  Assign <b className="text-white">{confirm.lead.name}</b>
                  <span className="text-slate-500"> ({confirm.lead.district}, {confirm.lead.loan_type})</span>
                  <br/>to <b className="text-amber-300">{confirm.staff.name}</b>
                  <span className="text-slate-500"> ({confirm.staff.district || "—"}
                  {confirm.staff.bank_name ? ` · ${confirm.staff.bank_name}` : ""})</span>?
                </div>
                <div className="text-[11px] text-slate-500">
                  This will move the case out of the open pool and notify the bank staff.
                  Only the assigned staff can then take this case.
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button data-testid="assign-cancel-btn" variant="ghost" onClick={() => setConfirm(null)} className="text-slate-300 hover:text-white">
                    Cancel
                  </Button>
                  <Button data-testid="assign-confirm-btn" onClick={doAssign}
                    disabled={!!assigning[confirm.lead.lead_id]}
                    className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full px-4 disabled:opacity-50">
                    {assigning[confirm.lead.lead_id]
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Assigning…</>
                      : <><UserCheck className="w-4 h-4 mr-2"/>Yes, Assign</>}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* --------- Reject Case dialog (admin only) --------- */}
      {user?.role === "admin" && (
        <Dialog open={!!rejectLead} onOpenChange={(o) => !o && !rejecting && setRejectLead(null)}>
          <DialogContent className="bg-slate-900 border-red-500/40 text-white max-w-md" data-testid="reject-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-red-400 flex items-center gap-2">
                <Ban className="w-5 h-5"/> Reject Case — {rejectLead?.lead_id}
              </DialogTitle>
            </DialogHeader>
            {rejectLead && (
              <div className="space-y-4">
                <div className="rounded-lg bg-slate-950/60 border border-slate-800 p-3">
                  <div className="text-white font-bold">{rejectLead.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {rejectLead.district} · {rejectLead.loan_type} · {rejectLead.phone || "—"}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Reason <span className="text-red-400">*</span></label>
                  <select
                    data-testid="reject-reason"
                    value={rejectForm.reason}
                    onChange={(e) => setRejectForm(f => ({ ...f, reason: e.target.value }))}
                    className="mt-1 w-full bg-slate-950 border border-slate-700 text-white rounded-md px-2 py-2 text-sm focus:outline-none focus:border-red-500"
                  >
                    <option value="">Select a reason…</option>
                    {rejectReasons.map(r => (
                      <option key={r.key} value={r.key}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Remarks <span className="text-slate-600">(optional)</span></label>
                  <Textarea
                    data-testid="reject-remarks"
                    value={rejectForm.remarks}
                    onChange={(e) => setRejectForm(f => ({ ...f, remarks: e.target.value }))}
                    placeholder="Why is this case being rejected?"
                    className="mt-1 bg-slate-950 border-slate-700 text-white min-h-[80px]"
                  />
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <Button
                    data-testid="reject-cancel"
                    variant="ghost"
                    disabled={rejecting}
                    onClick={() => setRejectLead(null)}
                    className="text-slate-300 hover:text-white"
                  >Cancel</Button>
                  <Button
                    data-testid="reject-confirm"
                    disabled={!rejectForm.reason || rejecting}
                    onClick={doReject}
                    className="bg-red-500 hover:bg-red-400 text-white font-bold rounded-full px-4 disabled:opacity-40"
                  >
                    {rejecting
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Rejecting…</>
                      : <><Ban className="w-4 h-4 mr-2"/>Confirm Reject</>}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Per-row Assign dropdown + gold Assign button. Same-district staff
// are floated to the top of the list so the operator picks the most
// relevant person by default.
// ------------------------------------------------------------------
function RowQuickMenu({ lead, staffList, onAssign, onReject, onDetails }) {
  const sameDist = (s) => (s.district || "").trim().toLowerCase() === (lead.district || "").trim().toLowerCase();
  const sorted = [...staffList].sort((a, b) => {
    const ad = sameDist(a) ? 0 : 1, bd = sameDist(b) ? 0 : 1;
    if (ad !== bd) return ad - bd;
    return (a.name || "").localeCompare(b.name || "");
  });
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`ibp-quick-${lead.lead_id}`}
          onClick={(e) => e.stopPropagation()}
          title="Quick actions"
          className="w-8 h-8 rounded-full bg-slate-800/70 hover:bg-amber-500/20 border border-slate-700 hover:border-amber-500/50 text-slate-400 hover:text-amber-300 flex items-center justify-center transition-colors"
        ><MoreVertical className="w-4 h-4"/></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="right" className="bg-slate-900 border-slate-700 text-white min-w-[220px]">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">
          <Zap className="w-3 h-3 inline mr-1 text-amber-400"/>{lead.name}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-slate-800"/>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="focus:bg-amber-500/20 text-xs cursor-pointer">
            <UserCheck className="w-3.5 h-3.5 mr-2 text-emerald-400"/> Assign to Staff
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="bg-slate-900 border-slate-700 text-white max-h-[60vh] overflow-y-auto max-w-[320px]">
              {sorted.length === 0 && <DropdownMenuItem disabled>No active bank staff</DropdownMenuItem>}
              {sorted.slice(0, 40).map(s => (
                <DropdownMenuItem key={s.id} onClick={(e) => { e.stopPropagation(); onAssign(s); }}
                  className="text-xs cursor-pointer focus:bg-amber-500/20"
                  data-testid={`ibp-quick-assign-${lead.lead_id}-${s.id}`}>
                  <span className={sameDist(s) ? "text-amber-300 font-bold" : ""}>
                    {(s.name || "?").toUpperCase()}
                  </span>
                  <span className="text-[10px] text-slate-400 ml-1">· {(s.district || "—").toUpperCase()} · {s.specialization || "General"}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onReject(); }}
          className="focus:bg-red-500/20 text-xs cursor-pointer"
          data-testid={`ibp-quick-reject-${lead.lead_id}`}>
          <Ban className="w-3.5 h-3.5 mr-2 text-red-400"/> Reject Case
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-slate-800"/>
        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDetails(); }}
          className="focus:bg-slate-800 text-xs cursor-pointer">
          <Eye className="w-3.5 h-3.5 mr-2 text-slate-400"/> View Details
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ------------------------------------------------------------------
// Per-row Assign dropdown + gold Assign button. Same-district staff
// are floated to the top of the list so the operator picks the most
// relevant person by default.
// ------------------------------------------------------------------
function AssignAction({ lead, staffList, selectedStaffId, onChangeStaff, onConfirm, assigning }) {
  const sameDist = (s) => (s.district || "").trim().toLowerCase() === (lead.district || "").trim().toLowerCase();
  const sorted = useMemo(() => {
    const list = [...staffList];
    list.sort((a, b) => {
      const ad = sameDist(a) ? 0 : 1;
      const bd = sameDist(b) ? 0 : 1;
      if (ad !== bd) return ad - bd;
      return (a.name || "").localeCompare(b.name || "");
    });
    return list;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffList, lead.district]);

  const staff = staffList.find(s => s.id === selectedStaffId) || null;

  if (lead.bank_assignment_status === "assigned") {
    return (
      <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold flex items-center gap-1 whitespace-nowrap"
           data-testid={`assign-done-${lead.lead_id}`}>
        <CheckCircle2 className="w-3 h-3"/> Assigned
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 min-w-[260px]">
      <select
        data-testid={`assign-dropdown-${lead.lead_id}`}
        value={selectedStaffId}
        onChange={(e) => onChangeStaff(e.target.value)}
        className="flex-1 bg-slate-950 border border-slate-700 text-white rounded-md px-2 py-1.5 text-xs max-w-[240px] focus:outline-none focus:border-amber-500"
      >
        <option value="">Assign to Staff — choose…</option>
        {sorted.length === 0 && <option disabled value="">No active bank staff</option>}
        {sorted.map(s => (
          <option key={s.id} value={s.id}>
            {(s.name || "?").toUpperCase()} - {(s.district || "—").toUpperCase()} - {s.specialization || "General"} Specialist ({s.score ?? 100}% Score){sameDist(s) ? " ·" : ""}
          </option>
        ))}
      </select>
      <Button
        data-testid={`assign-btn-${lead.lead_id}`}
        onClick={() => staff && onConfirm(staff)}
        disabled={!staff || assigning}
        className="bg-gradient-to-b from-amber-400 to-amber-600 hover:from-amber-300 hover:to-amber-500 text-slate-900 font-bold text-xs rounded-full px-3 py-1.5 shadow-md shadow-amber-500/30 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {assigning ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <><UserCheck className="w-3.5 h-3.5 mr-1"/>Assign</>}
      </Button>
    </div>
  );
}
